/* eslint-disable react/jsx-no-useless-fragment -- . */
import { useGetUserByEmailLazyQuery } from '@/affiliate-dashboard/graphql';
import { Avatar, AvatarFallback } from '@/components/base/avatar';
import { CountryPicker } from '@/components/base/form/country-picker';
import { FormField } from '@/components/base/form/form-field';
import { Input } from '@/components/base/form/input';
import { TextField } from '@/components/base/form/text-field';
import { PageHeading } from '@/components/base/page-heading';
import { enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type GeolocationQueryResult } from '../../../../graphql';
import { type Order } from './order-form-wrapper';

export enum PersonalInfoMode {
  EMPTY,
  FILLED,
  NOTFOUND,
}

export function PersonalInfoForm({
  geolocation,
  //isLoggedIn = false,
  watchCountry,
  changeMode,
  isUserFilled,
}: {
  geolocation: GeolocationQueryResult['data'] | null;
  //isLoggedIn?: boolean;
  watchCountry: string;
  changeMode: (mode: PersonalInfoMode) => void;
  isUserFilled: PersonalInfoMode;
}): JSX.Element {
  const { t } = useTranslation();
  const [getUserByEmail] = useGetUserByEmailLazyQuery();
  const methods = useFormContext<Order>();
  const watchEmail = methods.watch('email');
  const watchFirstname = methods.watch('firstName');
  const watchLastName = methods.watch('lastName');

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (!watchEmail) return;

      const isValid = await methods.trigger('email');
      if (!isValid) return;

      try {
        const { data } = await getUserByEmail({
          variables: {
            email: watchEmail,
          },
        });
        if (data?.userByEmail) {
          changeMode(PersonalInfoMode.FILLED);
          methods.reset({
            ...methods.getValues(),
            firstName: data.userByEmail.firstName ?? '',
            lastName: data.userByEmail.lastName ?? '',
            countryCode: data.userByEmail.countryCode ?? '',
          });
        } else {
          changeMode(PersonalInfoMode.NOTFOUND);
          methods.reset({
            ...methods.getValues(),
            firstName: '',
            lastName: '',
            countryCode: '',
          });
        }
      } catch (error) {
        changeMode(PersonalInfoMode.NOTFOUND);
        methods.reset({
          ...methods.getValues(),
          firstName: '',
          lastName: '',
          countryCode: '',
        });
        enqueueSnackbar('Error fetching user data. Please try again later.', {
          variant: 'error',
        });
      }
    }, 1000);

    return () => {
      clearTimeout(delayDebounce);
    };
  }, [watchEmail]);

  return (
    <div className="space-y-8">
      <PageHeading subheading={t('personalInfoForm.subheading')}>
        {t('personalInfoForm.heading')}
      </PageHeading>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="col-span-2 flex flex-col gap-2">
          <fieldset className="col-span-2">
            <FormField
              labelClassName="dark:text-white text-default-gray-950"
              label={t('personalInfoForm.email')}
              name="email"
            >
              <Input
                placeholder={t('personalInfoForm.emailPlaceholder')}
                inputSize="sm"
                variant="secondary"
                data-testid="email-input"
              />
            </FormField>
          </fieldset>
          {isUserFilled === PersonalInfoMode.FILLED ? (
            <div className="relative">
              <div className="flex h-full items-center justify-start gap-2">
                <Avatar>
                  <AvatarFallback>
                    {watchFirstname[0]}
                    {watchLastName[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden lg:block">
                  <h1 className="text-sm">{watchFirstname}</h1>
                  <p className="text-xs text-default-gray">{watchEmail}</p>
                </div>
              </div>
            </div>
          ) : null}
          {isUserFilled === PersonalInfoMode.NOTFOUND ? (
            <>
              <span className="text-primary">
                {t('affiliateOrder.userNotFound', { email: watchEmail })}
              </span>
            </>
          ) : null}
        </div>

        {isUserFilled === PersonalInfoMode.NOTFOUND ? (
          <>
            <fieldset className="col-span-2 md:col-span-1">
              <FormField
                labelClassName="dark:text-white text-default-gray-950"
                label={t('personalInfoForm.firstName')}
                name="firstName"
              >
                <Input
                  inputSize="sm"
                  variant="secondary"
                  placeholder={t('personalInfoForm.firstNamePlaceholder')}
                  data-testid="first-name-input"
                />
              </FormField>
            </fieldset>
            <fieldset className="col-span-2 md:col-span-1">
              <FormField
                labelClassName="dark:text-white text-default-gray-950"
                label={t('personalInfoForm.lastName')}
                name="lastName"
              >
                <Input
                  inputSize="sm"
                  variant="secondary"
                  placeholder={t('personalInfoForm.lastNamePlaceholder')}
                  data-testid="last-name-input"
                />
              </FormField>
            </fieldset>

            <fieldset className="col-span-2 md:col-span-1">
              <fieldset className="w-full">
                <TextField
                  labelClassName="dark:text-white text-default-gray-950"
                  label={t('registration.country')}
                >
                  <CountryPicker
                    disabled={Boolean(geolocation?.geolocation.country)}
                    data-testid="country-code-input"
                    name="countryCode"
                    defaultValue={watchCountry}
                  />
                </TextField>
              </fieldset>
            </fieldset>
            <fieldset className="col-span-2 md:col-span-1">
              <FormField
                labelClassName="dark:text-white text-default-gray-950"
                label={t('personalInfoForm.city')}
                name="city"
              >
                <Input
                  placeholder={t('personalInfoForm.cityPlaceholder')}
                  inputSize="sm"
                  variant="secondary"
                  data-testid="city-input"
                />
              </FormField>
            </fieldset>
            <fieldset className="col-span-2 md:col-span-1">
              <FormField
                labelClassName="dark:text-white text-default-gray-950"
                label={t('personalInfoForm.street')}
                name="street"
              >
                <Input
                  placeholder={t('personalInfoForm.streetPlaceholder')}
                  inputSize="sm"
                  variant="secondary"
                  data-testid="street-input"
                />
              </FormField>
            </fieldset>
            <fieldset className="col-span-2 md:col-span-1">
              <FormField
                labelClassName="dark:text-white text-default-gray-950"
                label={t('personalInfoForm.postalCode')}
                name="zip"
              >
                <Input
                  placeholder={t('personalInfoForm.postalCodePlaceholder')}
                  inputSize="sm"
                  variant="secondary"
                  data-testid="zip-input"
                />
              </FormField>
            </fieldset>

            <fieldset className="col-span-2">
              <FormField
                labelClassName="dark:text-white text-default-gray-950"
                label={t('personalInfoForm.password')}
                name="password"
              >
                <Input
                  type="password"
                  placeholder={t('personalInfoForm.passwordPlaceholder')}
                  inputSize="sm"
                  variant="secondary"
                  data-testid="password-input"
                />
              </FormField>
            </fieldset>
          </>
        ) : null}
      </div>
    </div>
  );
}
